import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateUsername, updatePassword } from '../lib/supabase';
import { GoldBtn } from './UI';

export default function ProfileModal({ open, onClose }) {
  const { user, profile, refreshProfile } = useAuth();
  const [tab, setTab]           = useState('username'); // 'username' | 'password'

  // Username tab
  const [newUsername, setNewUsername] = useState('');
  const [usernameMsg, setUsernameMsg] = useState({ text: '', ok: false });
  const [savingUser,  setSavingUser]  = useState(false);

  // Password tab
  const [newPass,     setNewPass]     = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [passwordMsg, setPasswordMsg] = useState({ text: '', ok: false });
  const [savingPass,  setSavingPass]  = useState(false);

  if (!open || !profile) return null;

  const handleUsernameUpdate = async () => {
    const trimmed = newUsername.trim();
    if (!trimmed) { setUsernameMsg({ text: 'Please enter a username.', ok: false }); return; }
    if (trimmed.length < 3) { setUsernameMsg({ text: 'Username must be at least 3 characters.', ok: false }); return; }
    if (trimmed === profile.username) { setUsernameMsg({ text: 'That is already your username.', ok: false }); return; }
    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) { setUsernameMsg({ text: 'Only letters, numbers, and underscores allowed.', ok: false }); return; }

    setSavingUser(true); setUsernameMsg({ text: '', ok: false });
    const { error } = await updateUsername(user.id, trimmed);
    if (error) {
      setUsernameMsg({ text: error.message, ok: false });
    } else {
      setUsernameMsg({ text: '✓ Username updated successfully!', ok: true });
      setNewUsername('');
      if (refreshProfile) refreshProfile();
    }
    setSavingUser(false);
  };

  const handlePasswordUpdate = async () => {
    if (!newPass) { setPasswordMsg({ text: 'Please enter a new password.', ok: false }); return; }
    if (newPass.length < 6) { setPasswordMsg({ text: 'Password must be at least 6 characters.', ok: false }); return; }
    if (newPass !== confirmPass) { setPasswordMsg({ text: 'Passwords do not match.', ok: false }); return; }

    setSavingPass(true); setPasswordMsg({ text: '', ok: false });
    const { error } = await updatePassword(newPass);
    if (error) {
      setPasswordMsg({ text: error.message, ok: false });
    } else {
      setPasswordMsg({ text: '✓ Password updated successfully!', ok: true });
      setNewPass(''); setConfirmPass('');
    }
    setSavingPass(false);
  };

  const inp = {
    width: '100%', background: 'var(--surface2)', border: '1px solid var(--border2)',
    borderRadius: 7, color: 'var(--text)', padding: '10px 14px',
    fontSize: 15, fontFamily: "'Crimson Text', serif", outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(4,4,6,0.88)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border2)',
        borderRadius: 14, width: '100%', maxWidth: 440,
        boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 22px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 20, margin: 0 }}>
              Account Settings
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 3 }}>@{profile.username}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', margin: '16px 0 0' }}>
          {['username', 'password'].map(t => (
            <button key={t} onClick={() => { setTab(t); setUsernameMsg({ text: '', ok: false }); setPasswordMsg({ text: '', ok: false }); }} style={{
              flex: 1, padding: '11px', background: 'none', border: 'none',
              borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent',
              color: tab === t ? 'var(--gold)' : 'var(--text-muted)',
              fontSize: 14, cursor: 'pointer', fontFamily: "'Crimson Text', serif",
              fontWeight: tab === t ? 600 : 400, transition: 'all 0.15s',
              textTransform: 'capitalize',
            }}>
              {t === 'username' ? '👤 Change Username' : '🔒 Change Password'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '22px' }}>

          {/* ── Username tab ── */}
          {tab === 'username' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6 }}>Current username</div>
                <div style={{ color: 'var(--text)', fontSize: 15, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 14px' }}>
                  @{profile.username}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6 }}>New username</div>
                <input
                  value={newUsername}
                  onChange={e => { setNewUsername(e.target.value); setUsernameMsg({ text: '', ok: false }); }}
                  placeholder="Enter new username"
                  style={inp}
                  onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                  onKeyDown={e => e.key === 'Enter' && handleUsernameUpdate()}
                />
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 5 }}>
                  Letters, numbers, underscores only. Min 3 characters.
                </div>
              </div>

              {usernameMsg.text && (
                <div style={{ padding: '9px 13px', borderRadius: 7, fontSize: 14,
                  background: usernameMsg.ok ? 'rgba(39,174,96,0.1)' : 'rgba(192,57,43,0.1)',
                  color: usernameMsg.ok ? 'var(--green)' : 'var(--red)',
                  border: `1px solid ${usernameMsg.ok ? 'rgba(39,174,96,0.2)' : 'rgba(192,57,43,0.2)'}`,
                }}>
                  {usernameMsg.text}
                </div>
              )}

              <GoldBtn onClick={handleUsernameUpdate} disabled={savingUser || !newUsername.trim()}>
                {savingUser ? 'Saving…' : 'Update Username'}
              </GoldBtn>
            </div>
          )}

          {/* ── Password tab ── */}
          {tab === 'password' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6 }}>New password</div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={newPass}
                    onChange={e => { setNewPass(e.target.value); setPasswordMsg({ text: '', ok: false }); }}
                    placeholder="Enter new password"
                    style={{ ...inp, paddingRight: 44 }}
                    onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                  />
                  <button onClick={() => setShowPass(p => !p)} style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: 'var(--text-muted)',
                    cursor: 'pointer', fontSize: 15, padding: 0,
                  }}>{showPass ? '🙈' : '👁'}</button>
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6 }}>Confirm new password</div>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={confirmPass}
                  onChange={e => { setConfirmPass(e.target.value); setPasswordMsg({ text: '', ok: false }); }}
                  placeholder="Re-enter new password"
                  style={inp}
                  onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                  onKeyDown={e => e.key === 'Enter' && handlePasswordUpdate()}
                />
              </div>

              {/* Password strength indicator */}
              {newPass && (
                <div>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    {[1,2,3,4].map(i => {
                      const strength = newPass.length >= 12 ? 4 : newPass.length >= 8 ? 3 : newPass.length >= 6 ? 2 : 1;
                      return <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= strength ? (strength <= 1 ? 'var(--red)' : strength <= 2 ? '#e67e22' : strength <= 3 ? '#f1c40f' : 'var(--green)') : 'var(--border2)', transition: 'background 0.2s' }} />;
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {newPass.length < 6 ? 'Too short' : newPass.length < 8 ? 'Weak' : newPass.length < 12 ? 'Good' : 'Strong'}
                  </div>
                </div>
              )}

              {passwordMsg.text && (
                <div style={{ padding: '9px 13px', borderRadius: 7, fontSize: 14,
                  background: passwordMsg.ok ? 'rgba(39,174,96,0.1)' : 'rgba(192,57,43,0.1)',
                  color: passwordMsg.ok ? 'var(--green)' : 'var(--red)',
                  border: `1px solid ${passwordMsg.ok ? 'rgba(39,174,96,0.2)' : 'rgba(192,57,43,0.2)'}`,
                }}>
                  {passwordMsg.text}
                </div>
              )}

              <GoldBtn onClick={handlePasswordUpdate} disabled={savingPass || !newPass || !confirmPass}>
                {savingPass ? 'Updating…' : 'Update Password'}
              </GoldBtn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
